import { TRADE_OPTION_STATUS } from 'utils/Trade';
import fetchAPI from 'utils/fetchAPI';

const handleJSON = res =>
  new Promise((resolve, reject) => {
    const r = res.clone();
    res
      .json()
      .then(json => resolve(json))
      .catch(e => {
        if (e instanceof SyntaxError) {
          r.text()
            .then(t => resolve(t))
            .catch(e => reject(e));
        } else {
          reject(e);
        }
      });
  });

const handleResponse = res => {
  if (res.status === 200) {
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.indexOf('application/json') !== -1) {
      return handleJSON(res);
    }
    return res.text();
  }
  throw new Error(res.status);
};

/**
 * CLEAR TRADE DATA
 */

export const CLEAR_TRADE_DATA = 'CLEAR_TRADE_DATA';

export const clearTradeData = () => ({
  type: CLEAR_TRADE_DATA,
});

/**
 * FETCH TRADES
 */
export const INIT_TRADE_LOADING = 'INIT_TRADE_LOADING';
export const FINISH_TRADE_LOADING = 'FINISH_TRADE_LOADING';

export const initTradeLoading = ({ page, size }) => ({
  type: INIT_TRADE_LOADING,
  page,
  size,
});

export const finishTradeLoading = ({ page, items, total }) => ({
  type: FINISH_TRADE_LOADING,
  page,
  items,
  total,
});

export const SAVE_QUERY = 'SAVE_QUERY';
export const saveQuery = query => ({
  type: SAVE_QUERY,
  query,
});

export const getTrades = ({
  channelId,
  page = 1,
  size,
  query = '', // page starts from 1
}) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    dispatch(initTradeLoading({ page, size }));
    const token = getState().users.token;
    const query = getState().trades.query;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms?per_page=${size}&page=${page}&q=${query}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleResponse(res))
      .then(json => {
        const { items, page, total } = json;
        const prevTotal = getState().trades.total;
        if (total !== prevTotal || page === 1) {
          dispatch(clearTradeData());
        }
        return dispatch(finishTradeLoading({ page, items, total }));
      });
  });

export const getTradeById = ({ channelId, formId }) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${formId}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const createTrade = ({ channelId, trade }) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;

    removeEmptyPhoneNumber(trade);

    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...trade }),
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const publishTrade = ({ channelId, tradeId }) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/publish`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const assignTradeManager = ({ channelId, tradeId, managerId }) => (
  dispatch,
  getState
) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/manager`,
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ manager_id: managerId }),
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const updateTradeCustomer = ({
  channelId,
  tradeId,
  full_name,
  phone_number,
  email,
  company,
  industry_type_id,
  user_id,
}) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/customer`,
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        full_name,
        phone_number,
        email,
        company,
        industry_type_id,
        user_id,
      }),
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const addTradeOption = ({ channelId, tradeId, option }) => (
  dispatch,
  getState
) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/options`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ option }),
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const editTradeOpion = ({
  channelId,
  tradeId,
  option: { id, ...rest },
}) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/options/${id}`,
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ ...rest }),
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const getTradeDocuments = ({ channelId, tradeId }) => (
  dispatch,
  getState
) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/documents`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const getTradeTasks = ({ channelId, tradeId }) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/tasks`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const addTradeTask = ({
  channelId,
  tradeId,
  subject,
  body,
  timestamp_to_send,
  to_emails,
  cc_emails,
  document_ids,
}) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/tasks`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subject,
        body,
        timestamp_to_send,
        to_emails,
        cc_emails,
        document_ids,
      }),
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const editTradeTask = ({
  channelId,
  tradeId,
  taskId,
  subject,
  body,
  timestamp_to_send,
  to_emails,
  cc_emails,
  document_ids,
}) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/tasks/${taskId}`,
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subject,
        body,
        timestamp_to_send,
        to_emails,
        cc_emails,
        document_ids,
      }),
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const cancelTradeTask = ({ channelId, tradeId, taskId }) => (
  dispatch,
  getState
) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/tasks/${taskId}`,
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ is_cancelled: true }),
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const resendTradeTask = ({ channelId, tradeId, taskId }) => (
  dispatch,
  getState
) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/tasks/${taskId}/resend`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const changeTradeOptionStatus = ({
  channelId,
  tradeId,
  optionId,
  status,
}) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    // check status
    if (
      [TRADE_OPTION_STATUS.SUCCESSFUL, TRADE_OPTION_STATUS.INVALID].indexOf(
        status
      ) === -1
    ) {
      reject('Invalid status value');
    }
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}/options/${optionId}/status`,
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json), err => reject(err));
  });

export const linkChannelFolderItemsToTrade = ({
  channelId,
  tradeId,
  draftIds: channel_draft_ids,
  messageIds: channel_message_ids,
  broadcastMessageIds: channel_broadcast_message_ids,
}) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/channels/${channelId}/forms/${tradeId}`,
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        channel_draft_ids,
        channel_message_ids,
        channel_broadcast_message_ids,
      }),
    })
      .then(res => handleResponse(res))
      .then(
        json => resolve(json),
        err => {
          if (err instanceof SyntaxError) {
            return resolve();
          }
          return reject(err);
        }
      );
  });

export const searchProducts = ({ query }) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/trades/products?q=${query}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json.data), err => reject(err));
  });

export const searchPlaces = ({ query }) => (dispatch, getState) =>
  new Promise((resolve, reject) => {
    const token = getState().users.token;
    fetchAPI({
      endpoint: `/places?q=${query}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => handleResponse(res))
      .then(json => resolve(json.data), err => reject(err));
  });

export function removeEmptyPhoneNumber(trade) {
  if (trade.customer && 'phone_number' in trade.customer) {
    if (!trade.customer.phone_number) {
      delete trade.customer.phone_number;
    }
  }
}



// WEBPACK FOOTER //
// ./app/actions/TradeActionCreators.js